import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_VILLA } from 'constants/offer'
import { buildAvailableRateKey } from 'lib/offer/availabilityUtils'
import generateOccupancyStringByRoom from 'lib/offer/generateOccupancyStringByRoom'
import { getCalenderDaysByCheckIn } from 'lib/offer/offerCalendarUtils'

export interface LereCacheContext {
  calendarsByOccupancyLereGroups?: App.CalendarState['calendarsByOccupancyLereGroups']
  offerAvailableRatesLereGroups?: App.OfferState['offerAvailableRatesLereGroups']
  expectedLereGroup?: string
}

function shouldEnforceLereGroup(context: LereCacheContext | undefined): boolean {
  return !!context && 'expectedLereGroup' in context
}

function cachedLereGroupMatches(
  cachedLereGroup: string | undefined,
  context: LereCacheContext | undefined,
): boolean {
  return !shouldEnforceLereGroup(context) || cachedLereGroup === context?.expectedLereGroup
}

function getLEPackageItemTotals(
  offer: App.Offer | App.BundledOffer,
  pkg: App.Package,
  checkIn: string,
  checkOut: string,
  occupancy: App.Occupants,
  calendarsByPackageKey: App.CalendarState['calendarsByPackageKey'] | undefined,
  calendarsByOccupancy: App.CalendarState['calendarsByOccupancy'] | undefined,
  availableRatesByOccupancy: { [offerId: string]: App.OfferAvailableRatesByOccupancy },
  options: {
    calendarsByPackageKeyLereGroups?: App.CalendarState['calendarsByPackageKeyLereGroups']
    calendarsByOccupancyLereGroups?: App.CalendarState['calendarsByOccupancyLereGroups']
    offerAvailableRatesLereGroups?: App.OfferState['offerAvailableRatesLereGroups']
    lereGroup?: string
  } = {},
  lereCacheContext?: LereCacheContext,
): App.Checkout.ItemViewTotals | undefined {
  let surcharge = 0
  let extraGuestSurcharge = 0
  let price = 0
  let memberPrice = 0
  let value = 0
  let memberValue = 0
  let propertyFees = 0
  let taxesAndFees = 0
  let extraGuestSurchargesPayableAtProperty = false
  let isAvailable = false
  let dynamicPricingSurchargeIncludedInSource = false
  if (
    (offer.type === OFFER_TYPE_ALWAYS_ON || offer.type === OFFER_TYPE_VILLA) &&
    (offer.property?.useDynamicOccupancy || offer.property?.useDynamicCancellationPolicies)
  ) {
    const key = buildAvailableRateKey(checkIn, checkOut, [occupancy])
    const cachedLereGroup = lereCacheContext?.offerAvailableRatesLereGroups?.[offer.id]?.[key]
    if (!cachedLereGroupMatches(cachedLereGroup, lereCacheContext)) {
      return undefined
    }

    const availableRates = availableRatesByOccupancy[offer.id]?.[key]?.rates
    const availableRate = availableRates?.find(rate => rate.packageUniqueKey === pkg.uniqueKey)
    const ratesLereGroup = options.offerAvailableRatesLereGroups?.[offer.id]?.[key]

    if (!availableRate) {
      return undefined
    }
    dynamicPricingSurchargeIncludedInSource = !!options.lereGroup && ratesLereGroup === options.lereGroup
    extraGuestSurcharge = availableRate.extraGuestSurcharge
    surcharge += availableRate.surcharge + extraGuestSurcharge
    price = availableRate.hotelPrice
    memberPrice = availableRate.hotelMemberPrice
    propertyFees = availableRate.propertyFees
    value = availableRate.hotelValue + propertyFees
    memberValue = availableRate.hotelMemberValue ? availableRate.hotelMemberValue + propertyFees : 0
    taxesAndFees = availableRate.taxesAndFees
    isAvailable = true
  } else {
    extraGuestSurchargesPayableAtProperty = !!pkg.roomRate?.extraGuestSurcharges.length && pkg.roomRate?.extraGuestSurcharges.some(surcharge => surcharge?.payableAtProperty)
    const occupancyKey = generateOccupancyStringByRoom(occupancy)
    const occupancyCalendar = calendarsByOccupancy?.[occupancyKey]
    const cachedLereGroup = lereCacheContext?.calendarsByOccupancyLereGroups?.[occupancyKey]?.[pkg.uniqueKey]
    if (!cachedLereGroupMatches(cachedLereGroup, lereCacheContext)) {
      return undefined
    }

    const pkgCalendar = occupancyCalendar?.[pkg.uniqueKey] ?? calendarsByPackageKey?.[pkg.uniqueKey]
    const calendarLereGroup = occupancyCalendar?.[pkg.uniqueKey] ?
      options.calendarsByOccupancyLereGroups?.[occupancyKey]?.[pkg.uniqueKey] :
      options.calendarsByPackageKeyLereGroups?.[pkg.uniqueKey]
    const pkgByDay = pkgCalendar && getCalenderDaysByCheckIn(pkgCalendar)[checkIn]

    if (!pkgByDay) {
      return undefined
    }

    dynamicPricingSurchargeIncludedInSource = !!options.lereGroup && calendarLereGroup === options.lereGroup
    extraGuestSurcharge = pkgByDay.extraGuestSurcharge ?? 0
    surcharge += pkgByDay.surcharge + (!extraGuestSurchargesPayableAtProperty ? extraGuestSurcharge : 0)
    price = pkgByDay.hotelPrice
    memberPrice = pkgByDay.hotelMemberPrice
    propertyFees = pkgByDay.propertyFees ?? 0
    value = pkgByDay.hotelValue + propertyFees
    memberValue = pkgByDay.hotelMemberValue ? pkgByDay.hotelMemberValue + propertyFees : 0
    taxesAndFees = pkgByDay.taxesAndFees
    isAvailable = !!pkgByDay.hotelAvailable
  }

  return {
    surcharge,
    price,
    memberPrice,
    value,
    memberValue,
    taxesAndFees,
    extraGuestSurcharge,
    otherFees: {
      propertyFees,
      extraGuestSurcharge: extraGuestSurchargesPayableAtProperty ? extraGuestSurcharge : 0,
    },
    isAvailable,
    dynamicPricingSurchargeIncludedInSource,
  }
}

export default getLEPackageItemTotals
